import { Heart, Car, Star, Map as MapIcon, Gem, Crown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@/lib/i18n";

interface BadgeIconProps {
  badgeType: "vonst_week" | "route_week" | "kringloop_expert" | "route_planner" | "insider" | "power_user";
  className?: string;
  size?: number;
}

export function BadgeIcon({ badgeType, className = "", size = 14 }: BadgeIconProps) {
  if (badgeType === "vonst_week") {
    return (
      <Heart
        className={`text-primary fill-primary ${className}`}
        size={size}
      />
    );
  }

  if (badgeType === "route_week") {
    return (
      <Car
        className={`text-primary ${className}`}
        size={size}
      />
    );
  }

  if (badgeType === "kringloop_expert") {
    return (
      <Star
        className={`text-primary fill-primary ${className}`}
        size={size}
      />
    );
  }

  if (badgeType === "route_planner") {
    return (
      <MapIcon
        className={`text-primary ${className}`}
        size={size}
      />
    );
  }

  if (badgeType === "insider") {
    return (
      <Gem
        className={`text-primary ${className}`}
        size={size}
      />
    );
  }

  if (badgeType === "power_user") {
    return (
      <Crown
        className={`text-magenta fill-magenta ${className}`}
        size={size}
      />
    );
  }

  return null;
}

interface UserBadgesProps {
  badges: Array<{ badgeType: string; count: number }>;
  className?: string;
  size?: number;
}

/**
 * Display all badges for a user (shows all badge types they have earned)
 */
export function UserBadges({ badges, className = "", size = 14 }: UserBadgesProps) {
  const { t } = useI18n();

  if (!badges || badges.length === 0) {
    return null;
  }

  const validBadgeTypes = ["vonst_week", "route_week", "kringloop_expert", "route_planner", "insider", "power_user"];

  return (
    <TooltipProvider>
      <div className={`flex items-center gap-1 ${className}`}>
        {badges.map((badge) => {
          if (validBadgeTypes.includes(badge.badgeType)) {
            // Check translation for badge type, fallback to capitalized string
            const label = t(`badges.${badge.badgeType}`) || badge.badgeType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

            return (
              <Tooltip key={badge.badgeType}>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <BadgeIcon
                      badgeType={badge.badgeType as any}
                      size={size}
                      className={badge.count > 0 ? "opacity-100" : "opacity-40"}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs font-medium">
                    {label}{badge.count > 1 ? ` (${badge.count}x)` : ""}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          }
          return null;
        })}
      </div>
    </TooltipProvider>
  );
}
